import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * FRAME
 *
 * An embedded document: a PDF, the customer's published site, the studio's
 * live preview. What is inside is not this library's UI, but the box it sits
 * in is — three pages each wrote the `<iframe>` by hand and each removed the
 * browser's border their own way.
 *
 * A frame is a block that fills its width. It takes its height from `fill`
 * (the whole of a positioned parent), from `ratio`, or from the site as
 * placement glue — a viewport calculation, `flex-1`, `min-h-screen`.
 *
 * Aspect ratios written out, like MediaFrame's: `aspect-[${ratio}]` compiles
 * and produces no CSS.
 */
export const frameRecipe = cva([uiMarker, 'block w-full border-0'], {
  variants: {
    /** Cover a positioned parent edge to edge. */
    fill: {
      true: 'absolute inset-0 h-full',
      false: '',
    },
    ratio: {
      auto: '',
      '4/3': 'aspect-[4/3]',
      '3/2': 'aspect-[3/2]',
      '16/9': 'aspect-video',
      '1/1': 'aspect-square',
    },
    radius: {
      none: 'rounded-none',
      md: 'rounded-md',
      lg: 'rounded-lg',
    },
  },
  defaultVariants: { fill: false, ratio: 'auto', radius: 'none' },
});

export type FrameRecipeProps = VariantProps<typeof frameRecipe>;
